import { useState } from 'react';

/**
 * Décision d'un conflit de synchronisation (ADR-073 §5, écran demandé le 2026-10-24) : la saisie faite hors ligne
 * sur l'appareil est rejouée telle quelle contre l'état courant (`reapply`), ou écartée sans effet (`discard`) ;
 * la justification reste sur la tâche et au journal. Aucun identifiant technique à l'écran.
 */

export const OFFLINE_CONFLICT_TYPE = 'offline_conflict';

const ACTIONS: Readonly<Record<string, string>> = {
  CompleteWorkItem: 'Validation de la fiche',
  RejectWorkItem: 'Rejet de la tâche',
  RecordWeightDeduction: 'Déduction de poids',
  RecordFinding: 'Constat',
  RecordCondemnationLine: 'Ligne de saisie sanitaire',
};

const REASONS: Readonly<Record<string, string>> = {
  version: 'La tâche a changé au serveur pendant la saisie.',
  backdated: 'Le fait est daté de plus de 24 h avant sa saisie.',
};

interface ConflictContext {
  offline_command?: { command_name?: string; captured_at?: string; client_recorded_at?: string; work_item_id?: string };
  reason?: string;
}

export function OfflineConflictPanel({ context, busy, onDecide, onOpenTask }: { context: Readonly<Record<string, unknown>> | undefined; busy: boolean; onDecide: (decision: 'reapply' | 'discard', justification: string) => void; onOpenTask?: (workItemId: string) => void }) {
  const [justification, setJustification] = useState('');
  const conflict = (context ?? {}) as ConflictContext;
  const command = conflict.offline_command ?? {};
  const captured = command.captured_at ?? command.client_recorded_at;
  const ready = justification.trim().length >= 3;
  return (
    <section className="ay-card" aria-labelledby="ay-conflict-title" data-testid="offline-conflict">
      <h2 id="ay-conflict-title">Saisie hors ligne en conflit</h2>
      <dl className="ay-detail">
        <dt>Saisie</dt>
        <dd>{ACTIONS[command.command_name ?? ''] ?? 'Saisie de l’appareil'}</dd>
        {captured !== undefined && (
          <>
            <dt>Saisie le</dt>
            <dd>{new Date(captured).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</dd>
          </>
        )}
        <dt>Motif</dt>
        <dd>{REASONS[conflict.reason ?? ''] ?? 'L’état du serveur a changé.'}</dd>
      </dl>
      {command.work_item_id !== undefined && onOpenTask !== undefined && (
        <p>
          <button type="button" className="ay-link-button" onClick={() => onOpenTask(command.work_item_id!)}>
            Ouvrir la tâche d’origine
          </button>
        </p>
      )}
      <div className="ay-field">
        <label className="ay-field__label" htmlFor="conflict-justification">
          Justification
        </label>
        <textarea id="conflict-justification" rows={2} value={justification} onChange={(event) => setJustification(event.target.value)} disabled={busy} />
      </div>
      <div className="ay-actionbar" role="toolbar" aria-label="Décision du conflit">
        <button type="button" className="ay-button ay-button--danger" disabled={busy || !ready} onClick={() => onDecide('discard', justification.trim())}>
          Écarter
        </button>
        <button type="button" className="ay-button" disabled={busy || !ready} onClick={() => onDecide('reapply', justification.trim())}>
          Rejouer la saisie
        </button>
      </div>
    </section>
  );
}
